import { useState } from 'react'

import type { InterventionPlanning, JourPlanning } from '../../lib/api/types'
import { formatDate, formatHeure } from '../../lib/formatters'
import { EtatVide } from '../ui/Etats'

/**
 * Les interventions sans infirmier, groupées par jour.
 *
 * Elles restent **aussi** visibles dans la grille : ce panneau ne les en
 * retire pas, il les rassemble. Une intervention non affectée qui disparaîtrait
 * du planning ne serait pas « à réaffecter », elle serait perdue.
 */
export function PanneauAReaffecter({
  jours,
  onOuvrir,
}: {
  jours: JourPlanning[]
  onOuvrir: (intervention: InterventionPlanning) => void
}) {
  const [replie, setReplie] = useState(false)

  const parJour = jours
    .map((jour) => ({
      jour,
      interventions: jour.interventions.filter((i) => i.utilisateur_id === null),
    }))
    .filter((g) => g.interventions.length > 0)

  const total = parJour.reduce((s, g) => s + g.interventions.length, 0)

  return (
    <aside
      className="w-full shrink-0 rounded-lg border border-slate-200 bg-white lg:w-72"
      aria-label="Interventions à réaffecter"
    >
      <button
        type="button"
        onClick={() => setReplie((r) => !r)}
        aria-expanded={!replie}
        className="flex w-full items-center justify-between px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
      >
        <span className="text-sm font-semibold text-slate-800">
          À réaffecter
          <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
            {total}
          </span>
        </span>
        <span aria-hidden="true" className="text-slate-400">
          {replie ? '▸' : '▾'}
        </span>
      </button>

      {!replie ? (
        <div className="max-h-[32rem] overflow-y-auto border-t border-slate-100 p-3">
          {total === 0 ? (
            <EtatVide
              icone="✅"
              titre="Tout est affecté"
              description="Aucune intervention en attente cette semaine."
            />
          ) : (
            <div className="space-y-4">
              {parJour.map(({ jour, interventions }) => (
                <div key={jour.date}>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {jour.libelle} {formatDate(jour.date).slice(0, 5)}
                  </p>
                  <ul className="space-y-1">
                    {interventions.map((intervention) => (
                      <li key={intervention.id}>
                        <button
                          type="button"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', intervention.id)
                            e.dataTransfer.effectAllowed = 'move'
                          }}
                          onClick={() => onOuvrir(intervention)}
                          className="w-full rounded border border-dashed border-slate-300 bg-slate-50 px-2 py-1.5 text-left text-xs hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                        >
                          <span className="font-medium tabular-nums text-slate-700">
                            {formatHeure(intervention.heure_debut)}
                          </span>{' '}
                          <span className="text-slate-800">
                            {intervention.patient_nom} {intervention.patient_prenom.charAt(0)}.
                          </span>
                          <span className="mt-0.5 flex flex-wrap gap-0.5">
                            {intervention.soins.map((s) => (
                              <span
                                key={s.type_soin_id}
                                className="rounded px-1 text-[9px] font-medium text-white"
                                style={{ backgroundColor: s.couleur }}
                              >
                                {s.code}
                              </span>
                            ))}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </aside>
  )
}
